import {
    type BlockRange,
    isBlockRangeSelection,
    type SelectionRange,
    type TextRange,
} from '@gitbook/slate';

import findSlateDOMNode from './find-dom-node';
import findDOMPoint from './find-dom-point';

/*
 * Find a native DOM range Slate `range`.
 */
export default function findDOMRange(range: SelectionRange, win: Window): Range | null {
    return isBlockRangeSelection(range)
        ? findDOMBlockRange(range, win)
        : findDOMTextRange(range, win);
}

function findDOMTextRange(range: TextRange, win: Window): Range | null {
    const { anchorKey, anchorOffset, focusKey, focusOffset, isBackward, isCollapsed } = range;

    const anchor = findDOMPoint(anchorKey!, anchorOffset, win);
    const focus = isCollapsed ? anchor : findDOMPoint(focusKey!, focusOffset, win);
    if (!anchor || !focus) {
        return null;
    }

    const r = win.document.createRange();
    const start = isBackward ? focus : anchor;
    const end = isBackward ? anchor : focus;
    r.setStart(start.node, start.offset);
    r.setEnd(end.node, end.offset);
    return r;
}

function findDOMBlockRange(range: BlockRange, win: Window): Range | null {
    if (!range.anchorBlock || !range.focusBlock) {
        return null;
    }

    const anchorNode = findSlateDOMNode(range.anchorBlock, win);
    const focusNode = findSlateDOMNode(range.focusBlock, win);
    if (!anchorNode || !focusNode) {
        return null;
    }

    const isBackward = range.isCollapsed
        ? false
        : Boolean(anchorNode.compareDocumentPosition(focusNode) & Node.DOCUMENT_POSITION_PRECEDING);

    const startNode = isBackward ? focusNode : anchorNode;
    const endNode = isBackward ? anchorNode : focusNode;

    const result = win.document.createRange();
    result.setStartBefore(startNode);
    result.setEndAfter(endNode);
    return result;
}
